import React, { useEffect, useMemo, useReducer } from 'react';

import { useAppDispatch, useAppSelector, useWidgetParam } from '@src/core/hooks';
import { changeLocation } from '@src/core/store';
import { useWidgetId } from '@src/core/IdProvider';
import { setStepCheckout } from '@src/stores/checkout';
import { track } from '@src/services/analytics';
import {
  areAllFormInputsFilled,
  collectFormInputValues,
  normalizeFormInputValue,
  type FormInputValue,
} from '@src/domains/orders';
import {
  clearRequiredFormInputsErrors,
  currentOrderSelector,
  orderRequiredFormInputsSelector,
  sendOrderRequiredFormInput,
  SENDING,
  COMPLETED,
} from '@src/features/orders/store/order';

interface InputsReducerState {
  [key: string]: FormInputValue;
}

export const useRequiredFormInputs = (proceedToNextStage: () => void) => {
  const orderRequiredFormInputs = useAppSelector(orderRequiredFormInputsSelector)!;
  const currentOrder = useAppSelector(currentOrderSelector);
  const dispatch = useAppDispatch();
  const checkoutPage = useWidgetParam('landing-page');
  const redirect = () => dispatch(changeLocation(checkoutPage || ''));
  const id = useWidgetId();

  React.useEffect(() => {
    dispatch(setStepCheckout({ id, value: 'personal' }));
  }, [id, dispatch]);

  const [inputsDataToSend, saveInputData] = useReducer(
    (state: InputsReducerState, payload: FormInputValue) => ({
      ...state,
      [payload.hash]: payload,
    }),
    {},
  );

  const handleInputDataToSend = (payload: FormInputValue) => {
    saveInputData(payload);

    // The buyer is fixing what the 422 rejected; the old message would stay under the new value.
    if (orderRequiredFormInputs.errors[payload.hash]) {
      dispatch(clearRequiredFormInputsErrors([payload.hash]));
    }
  };

  // The fields hand over only hash, value and type; the rules that shape the value live on the input.
  const validationRulesByHash = useMemo(
    () => Object.fromEntries(orderRequiredFormInputs.inputs.flat().map((input) => [input.hash, input.validationRules])),
    [orderRequiredFormInputs.inputs],
  );

  const finalInputs = collectFormInputValues(
    Object.fromEntries(
      Object.entries(inputsDataToSend).map(([hash, input]) => [
        hash,
        normalizeFormInputValue(input, validationRulesByHash[hash]),
      ]),
    ),
  );
  const isSending = orderRequiredFormInputs.state === SENDING;
  const isActionButtonDisabled = !areAllFormInputsFilled(finalInputs, orderRequiredFormInputs.qtd) || isSending;

  const handleSubmit = () => {
    dispatch(sendOrderRequiredFormInput(finalInputs, currentOrder!));
  };

  // The dep array is deliberately incomplete. `proceedToNextStage` is a fresh arrow on
  // every render in `CheckoutLegacy`, so adding it re-fires the effect after COMPLETED
  // and duplicates `checkoutCompleted`.
  useEffect(() => {
    if (orderRequiredFormInputs.state === COMPLETED) {
      proceedToNextStage();
    }
  }, [orderRequiredFormInputs.state]);

  useEffect(() => {
    track('checkoutBuyerInfoCollectAccess');
  }, []);

  return {
    inputs: orderRequiredFormInputs.inputs,
    handleInputDataToSend,
    handleSubmit,
    isActionButtonDisabled,
    isSending,
    redirect,
  };
};
